import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnDestroy, Output, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { getTimeSegments, TimeSlot } from '@thehubz/client/core';
import { DEFAULT_WORK_END_TIME, DEFAULT_WORK_START_TIME } from '@thehubz/shared/utils';
import { addHours, differenceInMinutes, format } from 'date-fns';
import { Subject, takeUntil } from 'rxjs';
import { DayAvailability } from '../../models';

@Component({
  selector: 'thehubz-day-availability-selector',
  templateUrl: './day-availability-selector.component.html',
  styleUrls: ['./day-availability-selector.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DayAvailabilitySelectorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DayAvailabilitySelectorComponent),
      multi: true,
    },
  ],
})
export class DayAvailabilitySelectorComponent implements ControlValueAccessor, Validator, OnDestroy {
  /**
   * Day of the week.
   */
  @Input() day = '';

  /**
   * Whether the form is in edit mode.
   */
  @Input() editMode = false;

  /**
   * Apply to whole week event.
   */
  @Output() applyToAll = new EventEmitter<TimeSlot[]>();

  /**
   * Whether the control is disabled
   */
  protected disabled = false;

  /**
   * Whether the control is touched
   */
  protected touched = false;

  /**
   * On touched callback
   */
  private _onTouched!: () => void;

  /**
   * Form group
   */
  formGroup = this.fb.nonNullable.group({
    selected: this.fb.nonNullable.control(true),
    timeSlots: this.fb.array([
      this.fb.nonNullable.group(
        {
          start: this.fb.nonNullable.control(DEFAULT_WORK_START_TIME),
          end: this.fb.nonNullable.control(DEFAULT_WORK_END_TIME),
        },
        {
          validators: timeSlotValidator,
        },
      ),
    ]),
  });

  /**
   * Slots form array control
   */
  get timeSlotsControl(): FormArray | null {
    if (!this.formGroup.get('selected')?.value) {
      return null;
    }
    return this.formGroup.get('timeSlots') as FormArray | null;
  }

  /**
   * Time segments (e.g. ['04:00', '04:30', '05:00', '05:30', ...])
   */
  timeSegments = getTimeSegments(7, 22, 2);

  /**
   * Whether the panel is expanded.
   */
  expanded = false;

  /**
   * Observable to emit when the component is destroyed.
   */
  private _destroyed$ = new Subject<void>();

  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) {}

  /**
   * Toggle the panel.
   */
  togglePanel() {
    this.expanded = !this.expanded;
    if (!this.timeSlotsControl?.controls.length) {
      return;
    }
  }

  /**
   * Add a new time slot.
   */
  addTimeSlot() {
    if (!this.timeSlotsControl) {
      return;
    }
    const lastTimeSlot = this.timeSlotsControl.controls[this.timeSlotsControl.controls.length - 1];
    const lastTimeSlotValue = lastTimeSlot.value as TimeSlot;
    const _start = lastTimeSlotValue.end;
    const _end = this._addHour(_start);
    const newTimeSlot = this._createTimeSlotControl({
      start: lastTimeSlotValue.end,
      end: _end,
    });
    this.timeSlotsControl.push(newTimeSlot, { emitEvent: true });
  }

  /**
   * Remove a time slot.
   * @param index Index of the time slot to remove.
   */
  removeTimeSlot(index: number) {
    const timeSlots = this.formGroup.get('timeSlots') as FormArray;
    timeSlots.removeAt(index);
  }

  /**
   * Emit apply to whole week event with the current time slots.
   */
  applyToWholeWeek() {
    const timeSlots = this.formGroup.get('timeSlots')?.value as TimeSlot[];
    this.applyToAll.emit(timeSlots);
  }

  //#region ControlValueAccessor implementation

  writeValue(dayAvailability: DayAvailability): void {
    if (dayAvailability.selected) {
      this.formGroup.get('selected')?.patchValue(true, { emitEvent: false });
      if (!this.timeSlotsControl) {
        const timeSlotControl = this.fb.nonNullable.array([] as FormGroup[]);
        this.formGroup.addControl('timeSlots', timeSlotControl);
      }
      this.timeSlotsControl?.clear();
      dayAvailability.timeSlots?.forEach((timeSlot) => {
        this.timeSlotsControl?.push(this._createTimeSlotControl(timeSlot));
      });
    }

    this.formGroup.patchValue(
      {
        selected: dayAvailability.selected,
        timeSlots: this.timeSlotsControl?.value || [],
      },
      {
        emitEvent: true,
      },
    );
  }

  registerOnChange(fn: (val: DayAvailability[]) => void): void {
    this.formGroup.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe((availabilities) => {
      if (!availabilities.selected) {
        this.formGroup.get('timeSlots')?.patchValue([], { emitEvent: false });
        availabilities = {
          ...availabilities,
          timeSlots: [],
        };
      }
      fn(availabilities as DayAvailability[]);
    });
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
    if (!this.timeSlotsControl) {
      return;
    }
    if (disabled) {
      this.timeSlotsControl.disable();
    } else {
      this.timeSlotsControl.enable();
    }
    this.cdr.markForCheck();
  }
  //#endregion

  //#region Validator implementation

  validate(): ValidationErrors | null {
    if (this.formGroup.invalid) {
      return {
        invalidTimeSlot: true,
      };
    }
    return null;
  }

  //#endregion

  private _createTimesSlotsControl(timeSlots: TimeSlot[]): FormGroup[] {
    return timeSlots.map((timeSlot) => this._createTimeSlotControl(timeSlot));
  }

  private _createTimeSlotControl(timeSlot: TimeSlot): FormGroup<{
    start: FormControl<string>;
    end: FormControl<string>;
  }> {
    return this.fb.nonNullable.group({
      start: timeSlot.start,
      end: timeSlot.end,
    });
  }

  /**
   * Add 1 hour to the time slot end time.
   * @param timeString Time string to add 1 hour to (e.g. '4:00').
   */
  private _addHour(timeString: string): string {
    const date = new Date();
    const time = new Date(date.getFullYear(), date.getMonth(), date.getDate(), +timeString.split(':')[0], +timeString.split(':')[1]);
    const newTime = addHours(time, 1);
    return format(newTime, 'HH:mm');
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}

/**
 * Custom validator to check if the time slot duration is at least 30 minutes
 */
function timeSlotValidator(control: AbstractControl): ValidationErrors | null {
  const start = control.get('start')?.value;
  const end = control.get('end')?.value;
  if (!start || !end) {
    return { duration: true };
  }
  const date = new Date();
  const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), +start.split(':')[0], +start.split(':')[1]);
  const endDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), +end.split(':')[0], +end.split(':')[1]);
  const diffInMinutes = differenceInMinutes(endDate, startDate);
  if (diffInMinutes < 30) {
    return { duration: true };
  }
  return null;
}
